import { type SessionChunk } from '../contracts';
import { getSafeErrorFields } from '../errors';
import { logger } from '../useSessionRecordingStore/lib/logger';
import { peekRecordingRow } from './recordingRowCache';
import { getRecordingRow } from './store';
import { chunkPk, type ChunkKey, type RecordingKey } from './types';

// Chunks that exhausted their upload retries, kept for a later drain —
// without this the blob dies with the upload chain.
const retained = new Map<string, SessionChunk>();
const dispatches = new Map<string, number>();
let retainedBytes = 0;

// Memory, not IDB quota: a full consult is single-digit MB.
export const RETRY_BUFFER_MAX_BYTES = 64 * 1024 * 1024;

// Drains are poked after every successful chunk; a 413/403 would otherwise
// re-upload for the rest of the session. Three is the chunk's whole budget
// before it is stranded — enough to outlast a blip, cheap enough to give up on.
export const RETRY_BUFFER_MAX_DISPATCHES = 3;

const bufferKey = (key: ChunkKey): string => chunkPk(key).join(':');

const readStoredRow = async (chunk: SessionChunk) => {
  try {
    return await getRecordingRow(chunk);
  } catch (err) {
    // Unusable storage is what the buffer exists for, but a recording no
    // source knows has no drain that would read its chunks. Refused, not lost.
    logger.warn({
      event_type: 'RETRY_BUFFER_RECORDING_READ_FAILED',
      recording_id: chunk.recordingId,
      details: {
        sessionId: chunk.sessionId,
        index: chunk.index,
        ...getSafeErrorFields(err),
      },
    });
    return undefined;
  }
};

/**
 * Over the cap, admission is refused and counted — eviction is silent loss.
 * Callers hold the chunk's upload slot, so its release notifies `subscribeInFlightUploads`.
 */
export const retainFailedChunk = async (chunk: SessionChunk): Promise<void> => {
  // Mirror first, database second — as the drain's dispatch does. The mirror
  // holds only what this page created, so a recovery-drained recording is
  // durable and unmirrored.
  if (!peekRecordingRow(chunk) && !(await readStoredRow(chunk))) return;
  const key = bufferKey(chunk);
  if (retained.has(key)) return;
  if (retainedBytes + chunk.size > RETRY_BUFFER_MAX_BYTES) {
    logger.warn({
      event_type: 'RETRY_BUFFER_ADMISSION_REFUSED',
      recording_id: chunk.recordingId,
      details: { index: chunk.index, size: chunk.size, retainedBytes },
    });
    return;
  }
  retained.set(key, chunk);
  retainedBytes += chunk.size;
  logger.info({
    event_type: 'CHUNK_RETAINED_FOR_RETRY',
    recording_id: chunk.recordingId,
    details: { index: chunk.index, size: chunk.size, retainedBytes },
  });
};

export const releaseRetainedChunk = (key: ChunkKey): void => {
  const id = bufferKey(key);
  const chunk = retained.get(id);
  if (!chunk) return;
  retained.delete(id);
  dispatches.delete(id);
  retainedBytes -= chunk.size;
};

/** Counts a drain dispatch so a deterministic failure cannot retry forever. */
export const noteRetainedDispatch = (key: ChunkKey): void => {
  const id = bufferKey(key);
  const chunk = retained.get(id);
  // The dispatch that uploaded it released it; a count for a chunk the buffer
  // no longer holds would outlive the blob and exhaust its next retention.
  if (!chunk) return;
  const count = (dispatches.get(id) ?? 0) + 1;
  dispatches.set(id, count);
  // The count is its own once-guard: it returns to zero only on release,
  // forget or reset, which is when a chunk may be reported again.
  if (count !== RETRY_BUFFER_MAX_DISPATCHES) return;
  logger.warn({
    event_type: 'RETRY_BUFFER_DISPATCH_CAP_REACHED',
    recording_id: key.recordingId,
    details: {
      sessionId: key.sessionId,
      index: key.index,
      size: chunk.size,
      retainedBytes,
    },
  });
};

/** Past the dispatch cap the blob stays retained — refuse-don't-evict — but idle. */
export const isRetainedDispatchExhausted = (key: ChunkKey): boolean =>
  (dispatches.get(bufferKey(key)) ?? 0) >= RETRY_BUFFER_MAX_DISPATCHES;

export const listRetainedChunks = (): SessionChunk[] => Array.from(retained.values());

/**
 * Audio a drain can still deliver — to one session's note, or to any when
 * unscoped. Exhausted is settled: its blob stays retained for the logout purge
 * but no drain will send it.
 */
const isDrainable = (chunk: SessionChunk, sessionId?: string): boolean =>
  (sessionId === undefined || chunk.sessionId === sessionId) &&
  !isRetainedDispatchExhausted(chunk);

export const hasDrainableRetainedChunks = (sessionId?: string): boolean =>
  listRetainedChunks().some((chunk) => isDrainable(chunk, sessionId));

/** Whether this page holds any undelivered chunk of the session, exhausted included. */
export const hasRetainedChunksForSession = (sessionId: string): boolean =>
  listRetainedChunks().some((chunk) => chunk.sessionId === sessionId);

// ---------------------------------------------------------------------------
// Audio the store cannot see
//
// The buffer holds blobs the store never took, so a ruling made from stored rows
// alone reads a hole where there is a live copy. Every such ruling must ask one
// of the two predicates below.
// ---------------------------------------------------------------------------

/**
 * One recording's undelivered audio. A sibling recording in the same session
 * gets its own completion and its own note, so its debt must not hold this
 * recording open.
 */
export const hasDrainableRetainedRecordingChunks = (key: RecordingKey): boolean =>
  listRetainedChunks().some(
    (chunk) =>
      chunk.sessionId === key.sessionId &&
      chunk.recordingId === key.recordingId &&
      isDrainable(chunk)
  );

/** One index whose only copy is in this page's memory. */
export const isDrainableRetainedChunk = (key: ChunkKey): boolean =>
  isRetainedChunk(key) && !isRetainedDispatchExhausted(key);

/** How much audio is left undelivered — what a wait that gave up has to report. */
export const countDrainableRetainedChunks = (sessionId?: string): number =>
  listRetainedChunks().filter((chunk) => isDrainable(chunk, sessionId)).length;

/** Re-check before a dispatch: a drain snapshot ages across its awaits. */
export const isRetainedChunk = (key: ChunkKey): boolean => retained.has(bufferKey(key));

/**
 * The highest index the buffer still holds for one recording; `null` when it
 * holds none. A retained chunk is audio that was produced, so a boundary below
 * it is too low whatever the row says — exhausted ones included.
 */
export const maxRetainedChunkIndex = (key: RecordingKey): number | null =>
  listRetainedChunks().reduce<number | null>(
    (max, chunk) =>
      chunk.sessionId === key.sessionId && chunk.recordingId === key.recordingId
        ? Math.max(max ?? chunk.index, chunk.index)
        : max,
    null
  );

export const getRetainedChunkBytes = (): number => retainedBytes;

/** Logout must leave nothing uploadable. */
export const resetRetryBuffer = (): void => {
  retained.clear();
  dispatches.clear();
  retainedBytes = 0;
};

/** A hard-deleted recording keeps nothing. */
export const forgetRetainedRecording = (key: {
  sessionId: string;
  recordingId: string;
}): void => {
  retained.forEach((chunk, id) => {
    // Match the chunk's own fields, not a second encoding of the key: a
    // reordered key builder would turn this purge into a silent no-op.
    if (chunk.sessionId !== key.sessionId || chunk.recordingId !== key.recordingId) {
      return;
    }
    retained.delete(id);
    dispatches.delete(id);
    retainedBytes -= chunk.size;
  });
};
